import React, { useMemo, useState } from "react"
import { getCssSizeValue, getFontSize, getIdWithoutSpecialSymbols, getPaddingsClassFromData, getPropSafely } from "@/utils/utils"
import styled from "styled-components"
import GridContainer from "@/components/common/GridContainer"
import { useGetCurrentStateDetails, useGetProperEnvKey } from "@/components/Forms/ContactForm/utils"
import Form from "@/components/Forms/Form"
import { useForm } from "@formspree/react"
import SubmissionModal from "@/components/Forms/SubmissionModal"
import useGlobals from "@/hooks/useGlobals"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { extractExtraFormValues, getInitialFormValues } from "@/components/Forms/utils"
import GoogleMap from "@/components/Maps/GoogleMap"
import useCurrentBranchData from "@/hooks/useCurrentBranchData"
import BranchSelect from "@/components/BranchSelect"
import { insetGridGapLeftCss } from "@/styles/globalStyles"
import { useIsDesktop } from "@/hooks/useDevice"
import Icon from "@/components/common/Icon/Icon"
import { GLOBAL_STATE, ICONS } from "@/constants"
import { useBranch } from "@/hooks/useBranch"

const StyledWrapper = styled(GridContainer)`
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		margin-left: var(--grid-margin-l) !important;
		margin-right: var(--grid-margin-r) !important;
	}
`

const StyledRightColumn = styled.div`
	display: none;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		display: block;
	}
`

const MapFullscreenModalContainer = styled.div`
	width: 100vw;
	height: 100dvh;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1000000;
`

const MobileDropdownAndMapWrapper = styled.div`
	display: flex;
	width: 100%;
	flex-direction: row;
	align-items: flex-start;
	height: fit-content;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		display: none;
	}
`

const GoogleMapContainer = styled.div`
	width: calc((3 / 8) * (100vw - 2 * var(--paddingX)) + var(--paddingX));
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		width: calc(100% + var(--paddingX));
		translate: calc(-1 * var(--paddingX)) 0;
	}

	&.mobile-hidden {
		display: none;
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
			display: block;
		}
	}
`

const MobileInfoBoxContainer = styled.div`
	width: 100vw;
	display: block;
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		display: none;
	}
`

const SingleMobileInfoBoxContainer = styled(GridContainer)`
	&.background {
		background: ${({ theme }) => theme.colors.platinum};
	}

	width: 100%;

	.header {
		font-size: ${getFontSize(18, BREAKPOINTS.MOBILE)};
		font-weight: ${({ theme }) => theme.fontWeight.bold};
		line-height: ${getFontSize(27, BREAKPOINTS.MOBILE)};
		margin-bottom: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
	}

	.list {
		line-height: 180%;
	}

	.form {
		grid-column: 1/9;
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
			grid-column: 2/8;
		}
	}
`

const MobileHeading = styled.div`
	font-size: ${getFontSize(18, BREAKPOINTS.MOBILE)};
	font-weight: ${({ theme }) => theme.fontWeight.bold};
	line-height: ${getFontSize(27, BREAKPOINTS.MOBILE)};
	margin-top: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		display: none;
	}
`

const StyledInputsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	row-gap: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(12, BREAKPOINTS.MOBILE)};

	.form-row {
		display: flex;
		gap: var(--grid-gap);
		width: 100%;
	}

	.select {
		width: 100%;
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
			width: calc(50% - 0.5 * var(--grid-gap));
		}
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		row-gap: ${getCssSizeValue(24, BREAKPOINTS.DESKTOP)};
		margin-bottom: ${getCssSizeValue(24, BREAKPOINTS.DESKTOP)};
	}
`

const StateSelectContainer = styled.div`
	position: relative;
	${insetGridGapLeftCss};
	width: calc(100% + var(--paddingX));
	padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} var(--paddingX) ${getCssSizeValue(8, BREAKPOINTS.MOBILE)}
		${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
	margin-bottom: ${getCssSizeValue(24)};
	color: ${({ theme }) => theme.colors.richBlack};
	background: ${({ theme }) => theme.colors.periwinkle};
	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(32)} var(--paddingX) ${getCssSizeValue(24)} ${getCssSizeValue(32)};
	}

	&.mobile {
		display: block;
		width: calc((5 / 8) * (100vw - 2 * var(--paddingX)) + var(--paddingX));
		height: calc((3 / 8) * (100vw - 2 * var(--paddingX)) + var(--paddingX));
		margin-bottom: 0;
		padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} var(--paddingX) ${getCssSizeValue(28, BREAKPOINTS.MOBILE)}
			${getCssSizeValue(24, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
			display: none;
		}
	}

	&.desktop {
		display: none;
		@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
			display: block;
		}
	}

	.text {
		margin-bottom: ${getCssSizeValue(8, BREAKPOINTS.MOBILE)};
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			margin-bottom: ${getCssSizeValue(24, BREAKPOINTS.DESKTOP)};
		}
	}

	.select-with-button {
		display: flex;
	}
`

const InfoBox = styled.div`
	width: 100%;
	margin-bottom: 0;
	//padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} ${getCssSizeValue(
		24,
		BREAKPOINTS.MOBILE,
	)} ${getCssSizeValue(40, BREAKPOINTS.MOBILE)};
	padding: ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} 0 ${getCssSizeValue(24, BREAKPOINTS.MOBILE)} 0;

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		margin-bottom: ${getCssSizeValue(48, BREAKPOINTS.DESKTOP)};
		padding: ${getCssSizeValue(24, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(24, BREAKPOINTS.DESKTOP)}
			${getCssSizeValue(24, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(40, BREAKPOINTS.DESKTOP)};
	}

	&.background {
		background: ${({ theme }) => theme.colors.platinum};
	}

	.header {
		@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
			font-weight: 600;
			margin-bottom: ${getCssSizeValue(24)};
		}
	}

	.list {
		display: flex;
		flex-direction: column;
		width: 45%;
	}
`

const CloseBtn = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	z-index: 1000001;
	--size: ${getCssSizeValue(42, BREAKPOINTS.MOBILE)};
	--svg-size: ${getCssSizeValue(16, BREAKPOINTS.MOBILE)};
	width: var(--size);
	height: var(--size);
	background: ${({ theme }) => theme.colors.chilliRed};

	cursor: pointer;
	display: grid;
	place-items: center;

	svg {
		width: var(--svg-size);
		height: var(--svg-size);

		path,
		circle {
			fill: #fff;
		}
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		--size: ${getCssSizeValue(40)};
		--svg-size: ${getCssSizeValue(16)};
	}
`

function getData(src) {
	return {
		id: getIdWithoutSpecialSymbols(getPropSafely(src, "id")),
		paddings: getPaddingsClassFromData(src),
		headerDescription: getPropSafely(src, "headerDescription", null),
		mobileHeading: getPropSafely(src, "mobileHeading"),
		submitButtonText: getPropSafely(src, "submitButtonText"),
		stateSelectHeader: getPropSafely(src, "stateSelectHeader"),
		contactHeader: getPropSafely(src, "contactHeader"),
		addressHeader: getPropSafely(src, "addressHeader"),
		inputs: getPropSafely(src, "formInputs", []),
	}
}

function useGetDataFilteredByStateOrFilteredByGlobalIsStateDataIsEmpty(data) {
	const { selectedOption } = useBranch()

	const filteredData = useMemo(() => {
		if (data?.length <= 0) return []

		const filterResult = data.filter((item) => item.state.handle.toLowerCase() === selectedOption)

		if (filterResult.length !== 0) {
			const localUseGlobalContext = filterResult[0]?.useGlobalContext
			if (!localUseGlobalContext) return filterResult
		}

		return data.filter((item) => item.state.handle.toLowerCase() === GLOBAL_STATE)
	}, [data, selectedOption])

	return useMemo(() => {
		if (!filteredData.length) return null
		return filteredData[0] || null
	}, [filteredData])
}

export default function ContactForm({ data: initialData }) {
	const data = useGetDataFilteredByStateOrFilteredByGlobalIsStateDataIsEmpty(initialData?.formModules || [], false)

	const formAPIKey = useGetProperEnvKey(true)

	const { paddings, submitButtonText, stateSelectHeader, contactHeader, mobileHeading, addressHeader, inputs } = getData(data)

	const states = useGlobals((state) => state.states)
	const allStates = states || []
	const currentState = useGetCurrentStateDetails(allStates)
	const currentBranch = useCurrentBranchData()
	const [modalOpen, setModalOpen] = useState(false)
	const [isMapModalOpen, setIsMapModalOpen] = useState(false)

	const { lat, lng } = useMemo(() => {
		return {
			lat: currentState?.geolocation?.latitude || 21,
			lng: currentState?.geolocation?.longitude || 50,
		}
	}, [currentState])

	const initialFormData = useMemo(() => {
		return getInitialFormValues(inputs)
	}, [])

	const [formData, setFormData] = useState(initialFormData)

	const [state, formSubmit] = useForm(formAPIKey, {
		data: extractExtraFormValues(formData),
	})

	const handleSubmit = (e) => {
		formSubmit(e)
		setModalOpen(true)
	}

	const { footer, contactFormModalHeader, contactFormModalIcon } = useGlobals()
	const modalCancelButtonText = footer?.newsletter?.modalCancelButtonText || ""
	const isDesktop = useIsDesktop()
	const aspectRatio = isDesktop ? 948 / 860 : 1
	return (
		<StyledWrapper id={getIdWithoutSpecialSymbols(initialData?.id || "")} className={`${paddings} no-margin`}>
			{isMapModalOpen && (
				<MapFullscreenModalContainer>
					<CloseBtn onClick={() => setIsMapModalOpen(false)}>
						<Icon name={ICONS.CLOSE} />
					</CloseBtn>
					<GoogleMap
						lat={lat}
						lng={lng}
						zoom={currentBranch.handle === "global" ? 6 : 12}
						apiKey={process.env.NEXT_GOOGLE_MAP_KEY}
						// style={{height: 'auto', width: '100%', aspectRatio: 948/860}}
						style={{ height: "100%", width: "100%" }}
						markers={{ lat, lng }}
					/>
				</MapFullscreenModalContainer>
			)}
			<SubmissionModal
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
				modalData={{
					closeButtonText: modalCancelButtonText,
					header: contactFormModalHeader || "",
					icon: contactFormModalIcon || {},
				}}
				loading={state.submitting}
			/>

			<MobileDropdownAndMapWrapper className="col-mobile-8">
				<StateSelectContainer className="mobile">
					<div className="text typo-strawford typo-strawford-84">{stateSelectHeader}</div>
					<div className="select-with-button">
						<BranchSelect />
					</div>
				</StateSelectContainer>
				<GoogleMapContainer onClick={() => setIsMapModalOpen(true)} className="col-desktop-8 col-tablet-4 col-mobile-3 map-wrapper">
					<GoogleMap
						lat={lat}
						lng={lng}
						zoom={currentBranch.handle === "global" ? 6 : 12}
						apiKey={process.env.NEXT_GOOGLE_MAP_KEY}
						// style={{height: 'auto', width: '100%', aspectRatio: 948/860}}
						style={{ height: "auto", width: "100%", aspectRatio: aspectRatio }}
						markers={{ lat, lng }}
					/>
				</GoogleMapContainer>
			</MobileDropdownAndMapWrapper>

			<MobileInfoBoxContainer>
				<SingleMobileInfoBoxContainer className="indent-mobile-1 no-margin padding">
					<InfoBox className="col-mobile-8">
						<div className="header typo-borna typo-borna-53">{addressHeader}</div>
						<div className="list typo-strawford typo-strawford-46">
							<div className="col-desktop-6">{currentState?.address || ""}</div>
						</div>
					</InfoBox>
				</SingleMobileInfoBoxContainer>
				<SingleMobileInfoBoxContainer className="background indent-mobile-1 no-margin padding">
					<InfoBox className="col-mobile-8 background">
						<div className="header typo-borna typo-borna-53">{contactHeader}</div>
						<div className="list typo-strawford typo-strawford-46">
							<div className="col-mobile-3">{currentState?.phoneNumber || ""}</div>
							<div className="col-mobile-3">{currentState?.email || ""}</div>
						</div>
					</InfoBox>
				</SingleMobileInfoBoxContainer>
				<SingleMobileInfoBoxContainer className="indent-mobile-1 no-margin padding">
					<MobileHeading className="col-mobile-8 typo-borna typo-borna-53">{mobileHeading}</MobileHeading>
				</SingleMobileInfoBoxContainer>
				<SingleMobileInfoBoxContainer className="no-margin padding">
					<Form
						groupingPattern={[1, 1, 1, 1]}
						formData={formData}
						setFormData={setFormData}
						handleSubmit={handleSubmit}
						inputs={inputs}
						submitButtonText={submitButtonText}
						LayoutWrapper={StyledInputsWrapper}
					/>
				</SingleMobileInfoBoxContainer>
			</MobileInfoBoxContainer>
			<GoogleMapContainer className="mobile-hidden col-desktop-8 col-tablet-4 col-mobile-3 map-wrapper">
				<GoogleMap
					lat={lat}
					lng={lng}
					zoom={currentBranch.handle === "global" ? 6 : 12}
					apiKey={process.env.NEXT_GOOGLE_MAP_KEY}
					// style={{height: 'auto', width: '100%', aspectRatio: 948/860}}
					style={{ height: "auto", width: "100%", aspectRatio: aspectRatio }}
					markers={{ lat, lng }}
				/>
			</GoogleMapContainer>
			<StyledRightColumn className="col-desktop-8 col-tablet-4">
				<StateSelectContainer className="desktop">
					<div className="text typo-strawford typo-strawford-84">{stateSelectHeader}</div>
					<div className="select-with-button">
						<BranchSelect />
					</div>
				</StateSelectContainer>

				<GridContainer className="no-margin typo-borna typo-borna-12">
					<InfoBox className="col-desktop-8">
						<div className="header typo-borna typo-borna-53">{addressHeader}</div>
						<div className="list typo-strawford typo-strawford-46">
							<div className="col-desktop-6">{currentState?.address || ""}</div>
						</div>
					</InfoBox>

					<InfoBox className="col-desktop-8 background">
						<div className="header typo-borna typo-borna-53">{contactHeader}</div>
						<div className="list typo-strawford typo-strawford-46">
							<div className="col-desktop-6">{currentState?.phoneNumber || ""}</div>
							<div className="col-desktop-6">{currentState?.email || ""}</div>
						</div>
					</InfoBox>
				</GridContainer>

				<Form
					groupingPattern={[1, 2, 1]}
					formData={formData}
					setFormData={setFormData}
					handleSubmit={handleSubmit}
					inputs={inputs}
					submitButtonText={submitButtonText}
					LayoutWrapper={StyledInputsWrapper}
				/>
			</StyledRightColumn>
		</StyledWrapper>
	)
}
